import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Grid,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  ShiftType,
  SHIFT_LABELS,
  UrgentTicket,
  CurrentTicket,
  MaintenanceItem,
} from '../types/shift';
import { listShiftSummaries, ShiftSummaryRecord } from '../services/shiftApi';

const SHIFT_COLORS: Record<ShiftType, string> = {
  NIGHT: '#5c6bc0',
  DAY: '#ffa726',
  EVENING: '#ab47bc',
};

// Normalize known name variants to a canonical form
const NAME_ALIASES: Record<string, string> = {
  'Ian Collins': 'Ian S Collins',
  'Steve Granter': 'Steven Granter',
};

function normalizeTechName(name: string): string {
  return NAME_ALIASES[name] || name;
}

const ShiftHistory: React.FC = () => {
  const { id: deepLinkId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTech, setFilterTech] = useState<string>('all');
  const [filterShift, setFilterShift] = useState<string>('all');
  const [filterCustomer, setFilterCustomer] = useState<string>('all');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [deepLinkNotFound, setDeepLinkNotFound] = useState(false);
  const deepLinkResolvedRef = useRef(false);

  const [summaries, setSummaries] = useState<ShiftSummaryRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSummaries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listShiftSummaries(100);
      setSummaries(data);
    } catch (err) {
      console.error('Failed to load shift summaries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  // Resolve /history/:id deep-link: expand the matching row once summaries are loaded.
  useEffect(() => {
    if (!deepLinkId || loading || deepLinkResolvedRef.current) return;
    deepLinkResolvedRef.current = true;
    const match = summaries.find((s) => s.id === deepLinkId);
    if (match) {
      setExpandedRow(match.id);
      // Clear any filters that would hide the entry so scroll has somewhere to land.
      setSearchQuery('');
      setFilterTech('all');
      setFilterShift('all');
      setFilterCustomer('all');
      setTimeout(() => {
        document.getElementById(`history-row-${match.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    } else {
      setDeepLinkNotFound(true);
      navigate('/history', { replace: true });
    }
  }, [deepLinkId, loading, summaries, navigate]);

  const filteredSummaries = useMemo(() => {
    return summaries.filter((s) => {
      if (filterTech !== 'all' && normalizeTechName(s.onDutyTech) !== filterTech) return false;
      if (filterShift !== 'all' && s.shift !== filterShift) return false;

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const allTicketsText = [s.urgentTicketsJson, s.currentTicketsJson, s.maintenanceItemsJson]
          .join(' ')
          .toLowerCase();
        if (
          !s.onDutyTech.toLowerCase().includes(q) &&
          !s.date.includes(q) &&
          !allTicketsText.includes(q) &&
          !(s.notes ?? '').toLowerCase().includes(q)
        )
          return false;
      }

      if (filterCustomer !== 'all') {
        const allTickets = [s.urgentTicketsJson, s.currentTicketsJson, s.maintenanceItemsJson].join(
          ' ',
        );
        if (!allTickets.toLowerCase().includes(filterCustomer.toLowerCase())) return false;
      }

      return true;
    });
  }, [summaries, filterTech, filterShift, filterCustomer, searchQuery]);

  const uniqueTechs = useMemo(() => {
    return [...new Set(summaries.map((s) => normalizeTechName(s.onDutyTech)))].sort();
  }, [summaries]);

  const parseTickets = (json: string | null | undefined): (UrgentTicket | CurrentTicket | MaintenanceItem)[] => {
    try {
      let parsed = JSON.parse(json ?? '[]');
      // Handle double-encoded JSON (string within string)
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const getTicketCounts = (s: ShiftSummaryRecord) => {
    const urgent = parseTickets(s.urgentTicketsJson) as UrgentTicket[];
    const current = parseTickets(s.currentTicketsJson) as CurrentTicket[];
    const maintenance = parseTickets(s.maintenanceItemsJson) as MaintenanceItem[];
    return { urgent: urgent.length, current: current.length, maintenance: maintenance.length };
  };

  return (
    <Box>
      {deepLinkNotFound && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setDeepLinkNotFound(false)}
        >
          Shift not found — it may have been deleted. Showing full history.
        </Alert>
      )}
      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Shift History
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              placeholder="Search tickets, techs, notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tech</InputLabel>
              <Select
                value={filterTech}
                label="Tech"
                onChange={(e: SelectChangeEvent) => setFilterTech(e.target.value)}
              >
                <MenuItem value="all">All Techs</MenuItem>
                {uniqueTechs.map((tech) => (
                  <MenuItem key={tech} value={tech}>
                    {tech}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Shift</InputLabel>
              <Select
                value={filterShift}
                label="Shift"
                onChange={(e: SelectChangeEvent) => setFilterShift(e.target.value)}
              >
                <MenuItem value="all">All Shifts</MenuItem>
                {(Object.entries(SHIFT_LABELS) as [ShiftType, string][]).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Customer</InputLabel>
              <Select
                value={filterCustomer}
                label="Customer"
                onChange={(e: SelectChangeEvent) => setFilterCustomer(e.target.value)}
              >
                <MenuItem value="all">All Customers</MenuItem>
                <MenuItem value="Flex">Flex</MenuItem>
                <MenuItem value="HCE">HCE</MenuItem>
                <MenuItem value="NAN">NAN</MenuItem>
                <MenuItem value="Hiboo">Hiboo</MenuItem>
                <MenuItem value="NOCLAND">NOCLAND</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
              {filteredSummaries.length} shift{filteredSummaries.length !== 1 ? 's' : ''}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.paper' }}>
              <TableCell width={40} />
              <TableCell>Date</TableCell>
              <TableCell>Shift</TableCell>
              <TableCell>On-Duty Tech</TableCell>
              <TableCell align="center">Tickets</TableCell>
              <TableCell align="center">Urgent</TableCell>
              <TableCell>Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSummaries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    {loading
                      ? 'Loading shift summaries...'
                      : summaries.length === 0
                        ? 'No shift summaries yet. Submit your first shift handoff from the Handoff Form.'
                        : 'No shifts match your filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSummaries.map((summary) => {
                const counts = getTicketCounts(summary);
                const isExpanded = expandedRow === summary.id;
                const urgent = parseTickets(summary.urgentTicketsJson) as UrgentTicket[];
                const current = parseTickets(summary.currentTicketsJson) as CurrentTicket[];
                const maintenance = parseTickets(summary.maintenanceItemsJson) as MaintenanceItem[];

                return (
                  <React.Fragment key={summary.id}>
                    <TableRow
                      id={`history-row-${summary.id}`}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '& > *': { borderBottom: isExpanded ? 0 : undefined },
                        scrollMarginTop: 80,
                      }}
                      onClick={() => setExpandedRow(isExpanded ? null : summary.id)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {new Date(summary.date + 'T00:00').toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={summary.shift}
                          size="small"
                          sx={{
                            bgcolor: SHIFT_COLORS[summary.shift],
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>{normalizeTechName(summary.onDutyTech)}</TableCell>
                      <TableCell align="center">
                        {counts.current + counts.maintenance}
                      </TableCell>
                      <TableCell align="center">
                        {counts.urgent > 0 ? (
                          <Chip label={counts.urgent} size="small" color="error" />
                        ) : (
                          <Typography color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {summary.submittedAt
                          ? new Date(summary.submittedAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Detail Row */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, px: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: 'rgba(0, 188, 212, 0.03)' }}>
                            {urgent.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="error" gutterBottom>
                                  URGENT TICKETS
                                </Typography>
                                {urgent.map((t, i) => (
                                  <Box key={i} sx={{ ml: 2, mb: 1 }}>
                                    <Typography variant="body2">
                                      <strong>{t.customer}</strong> — #{t.ticketNumber}{' '}
                                      {t.ticketSubject}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Action: {t.whatNeedsToBeDone}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {current.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                                  CURRENT TICKETS
                                </Typography>
                                {current.map((t, i) => (
                                  <Box key={i} sx={{ ml: 2, mb: 1 }}>
                                    <Typography variant="body2">
                                      <strong>{t.customer}</strong> — #{t.ticketNumber}{' '}
                                      {t.ticketSubject}
                                      {t.carriedForward && (
                                        <Chip
                                          label={`carried ${t.carriedForwardCount}x`}
                                          size="small"
                                          color="warning"
                                          sx={{ ml: 1 }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Done: {t.actionSummary} | Next: {t.nextSteps}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {maintenance.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                                  MAINTENANCE
                                </Typography>
                                {maintenance.map((m, i) => (
                                  <Box key={i} sx={{ ml: 2, mb: 1 }}>
                                    <Typography variant="body2">
                                      <strong>{m.customer}</strong> — #{m.ticketNumber}{' '}
                                      {m.description}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Status: {m.status}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {summary.notes && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  NOTES
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 2, whiteSpace: 'pre-line' }}>
                                  {summary.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ShiftHistory;
